import { ApolloClient } from 'apollo-client';
import { ApolloLink, Observable } from 'apollo-link';
import { InMemoryCache, NormalizedCacheObject } from 'apollo-cache-inmemory';
import { setContext } from 'apollo-link-context';
import { onError } from 'apollo-link-error';
import { RestLink } from 'apollo-link-rest';
import { useState, useEffect, useCallback } from 'react';
import {
  getAccessToken,
  getOrgId,
  getUserId,
} from '../Auth/auth';
import { getConfig } from '../config';
import useIpAddress from '../util-hooks/useIpAddress';
import { nrPageAction, nrNoticeError } from '../util-helpers/newrelic';
import { generateHeaders, refreshAccess } from '../Auth/FVID';
import { isNullOrUndefined, safeRedirect } from '../util-helpers/common';

const PLEASE_AUTHENTICATE_ERROR_MESSAGE = 'please authenticate';

const { hostUrl } = getConfig();

interface IApolloClientHook {
  client?: ApolloClient<NormalizedCacheObject>;
  restLink?: RestLink;
}

const getErrorLink = () =>
  onError(({ graphQLErrors, networkError, operation, response }) => {
    let handled = false;
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        // eslint-disable-next-line no-console
        nrPageAction('apolloClient', {
          message: `[Query error] Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}, Operation: ${operation.operationName}`,
          url: window.location.href
        });
      });
    }

    // HTML response detection
    if (response && response.errors && response.errors.length > 0) {
      const firstError = response.errors[0];
      // Check if error message contains HTML markers
      if (typeof firstError.message === 'string'
        && (firstError.message.includes('<!DOCTYPE html>')
          || firstError.message.includes('<html>')
          || firstError.message.startsWith('<'))) {

        // This is likely an HTML response being parsed as JSON
        nrNoticeError(new Error(firstError.message.substring(0, 100) + '...'), {
          type: 'HTMLInGraphQLResponse',
          operation: operation.operationName,
          url: window.location.href
        });

        return;
      }
    }

    if (networkError) {
      const errorMessage = networkError?.message?.toLocaleLowerCase();
      const pageUrl = window.location.href;

      let status: number | undefined;
      if (networkError && typeof networkError === 'object') {
        status = (
          // @ts-ignore - Handle various error formats
          networkError.statusCode
          // @ts-ignore - Handle axios-like errors
          || (networkError.response && networkError.response.status)
          // @ts-ignore - Handle fetch-like errors
          || networkError.status
        );
      }

      if (errorMessage?.includes('status code 401') || status === 401) {
        // Handle 401 Unauthorized errors
        refreshAccess(true, 'Authentication error - 401 Unauthorized');
        handled = true;
      } else if (errorMessage?.includes('status code 403') || status === 403) {
        // Handle 403 Forbidden errors
        nrPageAction('apolloClient', {
          message: `Forbidden Operation: ${operation.operationName} Network Error: ${networkError}`,
          url: pageUrl
        });
        refreshAccess(true, 'Authentication error - You do not have permission to access this resource');
        handled = true;
      } else if (errorMessage?.includes(PLEASE_AUTHENTICATE_ERROR_MESSAGE)) {
        // Handle explicit authentication messages
        nrPageAction('apolloClient', {
          message: `Unauthenticated Operation: ${operation.operationName} Network Error: ${networkError}`,
          url: pageUrl
        });
        safeRedirect(hostUrl);
        handled = true;
      } else if (errorMessage?.includes('AccessNotAllowedException')) {
        // Handle access exceptions
        nrPageAction('apolloClient', {
          message: `Unauthorized Operation: ${operation.operationName} Network Error: ${networkError}`,
          url: pageUrl
        });
        refreshAccess(true, 'Authentication error - You do not have access to this resource');
        handled = true;
      } else {
        // Handle any other network errors
        nrPageAction('apolloClient', {
          message: `Operation: ${operation.operationName} Network Error: ${networkError}`,
          url: pageUrl
        });
      }
    }

    // If we've handled the error, return an observable with a properly structured response
    // This prevents the error from propagating and causing an unhandled rejection
    if (handled) {
      return new Observable((observer: any) => {
        observer.next({
          data: {}, // Empty data object to prevent 'Cannot read properties of undefined (reading 'data')'
          errors: [{
            message: networkError ? networkError.message : 'An error occurred',
            extensions: { code: 'AUTH_ERROR' }
          }]
        });
        observer.complete();
        return () => {};
      });
    }
  });

const getRestLink = () => {
  const newConfig = getConfig();
  return new RestLink({ uri: newConfig.filevineApiBaseUrl });
};

const useApolloClient = (): IApolloClientHook => {
  const { clientIpAddress } = useIpAddress();

  const [state, setState] = useState<IApolloClientHook>({
    restLink: getRestLink(),
  });

  const getAuthLink = useCallback(() => setContext(async (_, { headers, ...rest }) => {
    try {
      if (isNullOrUndefined(getOrgId()) || isNullOrUndefined(getUserId())) {
        console.warn('OrgId or userId is not set. Retrieving new orgId and userId');
        await generateHeaders(true);
      }

      // Try to get the access token
      try {
        const accessToken = await getAccessToken();
        return {
          ...rest,
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-fv-clientIp': clientIpAddress,
            'x-fv-orgId': getOrgId(),
            'x-fv-userId': getUserId(),
            'x-fv-application': 'outlook-addin',
          },
        };
      } catch (error) {
        console.error('Error getting access token:', error);
        if (error === 'Unable to get access token') {
          refreshAccess(true, 'Authentication error - Unable to get access token');
        }

        // Instead of rejecting, return a context without auth headers
        // This will cause a 401 error which will be handled by getErrorLink
        return {
          ...rest,
          headers: {
            'x-fv-application': 'outlook-addin',
          },
        };
      }
    } catch (outerError) {
      // Catch any other errors that might occur
      console.error('Unexpected error in auth link:', outerError);
      return {
        ...rest,
        headers: {
          'x-fv-application': 'outlook-addin',
        },
      };
    }
  }), [clientIpAddress]);

  const getClient = useCallback(() => new ApolloClient({
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
      },
      query: {
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
    // this array needs to be in this specific order for the reverseCommentsLink to be called
    link: ApolloLink.from([
      getErrorLink(),
      getAuthLink(),
      getRestLink()
    ]),
  }), [getAuthLink]);

  useEffect(() => {
    setState((previousState) => ({
      ...previousState,
      client: getClient(),
    }));
  }, [getClient]);

  return state;
};

export default useApolloClient;
